/**
 * The admin body class (cas-c242).
 *
 * `_admin-body.scss` restyles the Quasar surfaces the moved admin bodies carry.
 * Page rules are DOM-scoped under `.admin-destination__body`; the teleported
 * layers (q-dialog, q-menu, q-tooltip render at <body>) cannot be, so they are
 * scoped by this class on <body> for the LIFETIME of a mounted destination.
 *
 * Daniel's hard limit: the contract must not bleed into the customer
 * experience. Two independent code paths keep the class off customer routes:
 *   1. AdminDestination sets it on mount and clears it on unmount.
 *   2. The `admin-hybrid-body-class.client` plugin clears it after every
 *      route change that lands outside /admin — even if (1) never ran.
 * Both are covered by body-class.test.ts, including a real vue-router
 * navigation from an admin route to a non-admin route.
 */
import { stripLocalePrefix } from '~/utils/public-audit-path';

export const ADMIN_BODY_CLASS = 'admin-hybrid-open';

type BodyLike = Pick<typeof document.body, 'classList'>;

export function setAdminBodyClass(on: boolean, body: BodyLike = document.body) {
  body.classList.toggle(ADMIN_BODY_CLASS, on);
}

/** `/admin...`, with or without the locale prefix (`/es/admin...`, cas-6e1f). */
export function isAdminPath(path: string): boolean {
  const p = stripLocalePrefix(path);
  return p === '/admin' || p.startsWith('/admin/') || p.startsWith('/admin?');
}

/** Route-change safety net: outside /admin the class is never allowed to linger. */
export function syncAdminBodyClassForPath(path: string, body: BodyLike = document.body) {
  if (!isAdminPath(path)) body.classList.remove(ADMIN_BODY_CLASS);
}
